/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useMergeRefs,
} from '@floating-ui/react';
import classNames from 'classnames';
import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { placementTranslation, type Popover } from '../popover';
import { StatusLabel } from '../status-label';
import { useNeedleTheme } from '../theme';
import { Typography } from '../typography';
import { useSpotlightContext } from './SpotlightProvider';

// Local instance context to pass stable ARIA ids to subcomponents
type SpotlightInstanceContextType = {
  headerId?: string;
  descriptionId?: string;
  setHeaderId?: (id: string | undefined) => void;
  setDescriptionId?: (id: string | undefined) => void;
};

const SpotlightInstanceContext =
  createContext<SpotlightInstanceContextType | null>(null);

const useSpotlightInstance = (): SpotlightInstanceContextType => {
  const ctx = useContext(SpotlightInstanceContext);
  if (ctx === null) {
    throw new Error(
      'Spotlight subcomponents must be used within <Spotlight />',
    );
  }
  return ctx;
};
import { useSpotlight } from './use-spotlight';

type SpotlightHeaderProps = {
  /** The spotlight header content */
  children?: React.ReactNode;
};

const SpotlightHeader = <T extends React.ElementType = 'div'>({
  children,
  className,
  as,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SpotlightHeaderProps>) => {
  const classes = classNames('ndl-spotlight-header', className);
  const { headerId, setHeaderId } = useSpotlightInstance();
  const Component: React.ElementType = as ?? 'div';

  useEffect(() => {
    if (Boolean(htmlAttributes?.id) === true) {
      setHeaderId?.(htmlAttributes?.id);
    }
  }, [setHeaderId, htmlAttributes?.id]);

  return (
    <Typography
      as={Component}
      className={classes}
      ref={ref}
      style={style}
      variant="title-4"
      {...restProps}
      htmlAttributes={{ ...htmlAttributes, id: headerId }}
    >
      {children}
    </Typography>
  );
};

SpotlightHeader.displayName = 'Spotlight.Header';

type SpotlightBodyProps = {
  /** The spotlight body content */
  children?: React.ReactNode;
};

const SpotlightBody = <T extends React.ElementType = 'div'>({
  children,
  className,
  as,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SpotlightBodyProps>) => {
  const classes = classNames('ndl-spotlight-body', className);
  const { descriptionId, setDescriptionId } = useSpotlightInstance();
  const Component: React.ElementType = as ?? 'div';

  useEffect(() => {
    if (Boolean(htmlAttributes?.id) === true) {
      setDescriptionId?.(htmlAttributes?.id);
    }
  }, [setDescriptionId, htmlAttributes?.id]);

  return (
    <Typography
      as={Component}
      className={classes}
      ref={ref}
      style={style}
      variant="body-medium"
      {...restProps}
      htmlAttributes={{ ...htmlAttributes, id: descriptionId }}
    >
      {children}
    </Typography>
  );
};

SpotlightBody.displayName = 'Spotlight.Body';

type SpotlightLabelProps = Omit<
  React.ComponentProps<typeof StatusLabel>,
  'color' | 'fill'
>;

const SpotlightLabel = <T extends React.ElementType = 'div'>({
  className,
  children,
  hasIcon,
  as,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SpotlightLabelProps>) => {
  useSpotlightContext();
  const classes = classNames('ndl-spotlight-label', className);

  return (
    <StatusLabel
      fill="semi-filled"
      variant="discovery"
      hasIcon={hasIcon}
      ref={ref}
      as={as}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </StatusLabel>
  );
};

SpotlightLabel.displayName = 'Spotlight.Label';

type SpotlightImageProps = {
  /** A string that represents the URL of the image */
  src: string;
  /** A string that represents the alt text of the image */
  alt: string;
};

const SpotlightImage = ({
  src,
  alt,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'img', SpotlightImageProps>) => {
  useSpotlightContext();
  const classes = classNames('ndl-spotlight-image n-body-medium', className);

  return (
    <img
      src={src}
      alt={alt}
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    />
  );
};

SpotlightImage.displayName = 'Spotlight.Image';

type SpotlightIconWrapperProps = {
  /** Content displayed in the icon wrapper */
  children?: React.ReactNode;
};

const SpotlightIconWrapper = <T extends React.ElementType = 'div'>({
  children,
  className,
  as,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SpotlightIconWrapperProps>) => {
  useSpotlightContext();
  const classes = classNames('ndl-spotlight-icon-wrapper', className);
  const Component = as ?? 'div';

  return (
    <Component
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

SpotlightIconWrapper.displayName = 'Spotlight.IconWrapper';

export type SpotlightCloseAction = 'clickOutside' | 'escapeKeyDown';

export type SpotlightProps = {
  /** The id of the `SpotlightTarget` that this `Spotlight` should be rendered next to */
  target: string;
  /** The actions to be displayed in the Spotlight. Should only be TextButton or OutlinedButton components. */
  actions?: React.ReactNode;
  /** A ReactNode that can be put in the left corner of the Spotlight (left of action buttons). It should be used to display the current step if you have a multi-step tour */
  beforeActions?: React.ReactNode;
  /** A callback function that is called if the Spotlight is closed by either clicking outside the Spotlight, or by pressing the Escape key. The function has one argument: action (string that can be either "clickOutside" or "escapeKeyDown") */
  onClose?: (action: SpotlightCloseAction) => void;
  /** Callback function called when the spotlight is opened */
  onOpen?: () => void;
  /** Children should be Spotlight subcomponents */
  children?: React.ReactNode;
} & Pick<
  React.ComponentProps<typeof Popover>,
  'placement' | 'initialFocus' | 'closeOnClickOutside'
>;

const SpotlightComponent = ({
  target,
  beforeActions,
  actions,
  initialFocus,
  onClose,
  placement,
  children,
  className,
  style,
  htmlAttributes,
  closeOnClickOutside = false,
  onOpen,
  ref,
  ...restProps
}: CommonProps<'div', SpotlightProps>) => {
  const generatedHeaderId = useId();
  const generatedDescriptionId = useId();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);

  const {
    hasOverlay,
    isActiveSpotlight,
    isOpen,
    registeredTargets,
    setActiveSpotlight,
    setIsOpen,
  } = useSpotlightContext();

  const [headerId, setHeaderId] = useState<string | undefined>(
    generatedHeaderId,
  );
  const [descriptionId, setDescriptionId] = useState<string | undefined>(
    generatedDescriptionId,
  );

  // Determine if a Header child exists
  const hasHeaderChild = React.useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    return childrenArray.some(
      (child) => React.isValidElement(child) && child.type === SpotlightHeader,
    );
  }, [children]);

  // Source ARIA from htmlAttributes
  const ariaLabel = htmlAttributes?.['aria-label'];
  const ariaLabelledBy =
    htmlAttributes?.['aria-labelledby'] ??
    (hasHeaderChild ? headerId : undefined);
  const ariaDescribedBy = htmlAttributes?.['aria-describedby'] ?? descriptionId;

  if (ariaLabel === undefined && !ariaLabelledBy) {
    console.warn(
      'The Spotlight is missing aria-label and Header. Please add one of them for accessibility.',
    );
  }

  const onOpenChange = (isOpen: boolean, event?: Event) => {
    if (!isOpen) {
      if (event instanceof PointerEvent) {
        onClose?.('clickOutside');
      } else if (event instanceof KeyboardEvent) {
        onClose?.('escapeKeyDown');
      }
      setActiveSpotlight(null);
      setIsOpen(false);
    }
  };

  const spotlight = useSpotlight({
    anchorElement: targetElement,
    closeOnClickOutside,
    initialFocus,
    isOpen: isActiveSpotlight(target) && isOpen && !!targetElement,
    onOpenChange,
    placement: placement ? placementTranslation[placement] : undefined,
  });

  const mergedRef = useMergeRefs([spotlight.refs.setFloating, ref]);

  const { themeClassName } = useNeedleTheme();
  const classes = classNames('ndl-spotlight', themeClassName, className);

  useEffect(() => {
    setTargetElement(registeredTargets[target]?.current);
  }, [target, registeredTargets]);

  useEffect(() => {
    if (isOpen && isActiveSpotlight(target)) {
      onOpen?.();
    }
  }, [isOpen, onOpen, isActiveSpotlight, target]);

  const findInitialFocus = () => {
    if (spotlight.initialFocus !== undefined) {
      return spotlight.initialFocus;
    }
    if (!actionsRef.current) {
      return undefined;
    }
    const focusableEls = actionsRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    return focusableEls.length - 1;
  };

  if (!spotlight.context.open) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={spotlight.context}
        modal={true}
        initialFocus={findInitialFocus()}
      >
        <SpotlightInstanceContext.Provider
          value={{
            descriptionId,
            headerId,
            setDescriptionId,
            setHeaderId,
          }}
        >
          {hasOverlay === true && (
            <FloatingOverlay
              className="ndl-spotlight-overlay ndl-spotlight-overlay-top"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />
          )}
          <div
            ref={mergedRef}
            className={classes}
            style={{
              ...spotlight.floatingStyles,
              ...spotlight.transitionStyles,
              ...style,
            }}
            {...spotlight.getFloatingProps({
              ...htmlAttributes,
              'aria-describedby': ariaDescribedBy,
              'aria-label': ariaLabel,
              'aria-labelledby': ariaLabelledBy,
              'aria-modal': 'true',
            })}
            {...restProps}
          >
            <div>{children}</div>
            {(Boolean(beforeActions) || Boolean(actions)) && (
              <div className="ndl-spotlight-footer">
                <Typography variant="body-medium">{beforeActions}</Typography>
                {Boolean(actions) && (
                  <div className="ndl-spotlight-actions" ref={actionsRef}>
                    {actions}
                  </div>
                )}
              </div>
            )}
          </div>
        </SpotlightInstanceContext.Provider>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

SpotlightComponent.displayName = 'Spotlight';

const Spotlight = Object.assign(SpotlightComponent, {
  Body: SpotlightBody,
  Header: SpotlightHeader,
  IconWrapper: SpotlightIconWrapper,
  Image: SpotlightImage,
  Label: SpotlightLabel,
});

export { Spotlight };
